/* eslint-disable camelcase */
import { TradingKeywordTypes } from 'store/modules/monitoring_store/types';
import { MonitoringPreferenceData } from 'store/modules/monitoring_preferences_store/types';
import { DEFAULT_MONITORING_SOUND } from './monitoring_sounds';

export type MonitoringPriority = 'alta' | 'media' | 'baixa';
export type MonitoringChannel = 'email' | 'sound' | 'push';

export const MONITORING_PRIORITIES: Array<{ value: MonitoringPriority; label: string }> = [
  { value: 'alta', label: 'Alta prioridade' },
  { value: 'media', label: 'Média prioridade' },
  { value: 'baixa', label: 'Baixa prioridade' },
];

export const MONITORING_CHANNELS: Array<{
  value: MonitoringChannel;
  label: string;
  help?: string;
}> = [
  {
    value: 'email',
    label: 'Email',
    help: '1 e-mail por pregão por dia, na primeira mensagem.',
  },
  {
    value: 'sound',
    label: 'Aviso sonoro',
    help: 'Para o aviso sonoro funcionar você precisa estar com o ConLicitação aberto em uma aba do seu navegador.',
  },
  { value: 'push', label: 'Área de trabalho' },
];

export const KEYWORD_CHANNELS: MonitoringChannel[] = ['sound', 'push'];

export const ALL_MESSAGES_PRIORITY: MonitoringPriority = 'baixa';
export const KEYWORD_PRIORITY: MonitoringPriority = 'alta';

export interface MonitoringRule {
  keywordId: number | null;
  label: string;
  priority: MonitoringPriority;
  channels: Record<MonitoringChannel, boolean>;
  active: boolean;
}

export const PREFERENCE_CHANNEL: Record<MonitoringChannel, string> = {
  email: 'email_notification',
  sound: 'sound_notification',
  push: 'push_notification',
};

export const ALL_MESSAGES_LABEL = 'Todas as mensagens do chat';

export const monitoringRules = (
  preference: MonitoringPreferenceData | null | undefined,
  keywords: TradingKeywordTypes[] | undefined,
): MonitoringRule[] => {
  const allMessages: MonitoringRule = {
    keywordId: null,
    label: ALL_MESSAGES_LABEL,
    priority: preference?.all_messages_priority || ALL_MESSAGES_PRIORITY,
    channels: {
      email: Boolean(preference?.email_notification),
      sound: Boolean(preference?.sound_notification),
      push: Boolean(preference?.push_notification),
    },
    active: Boolean(preference?.receive_notification_from_all_messages),
  };

  return [
    allMessages,
    ...(keywords || []).map((keyword) => ({
      keywordId: keyword.id,
      label: keyword.keyword,
      priority: keyword.priority || KEYWORD_PRIORITY,
      channels: {
        email: false,
        sound: keyword.notify_sound ?? allMessages.channels.sound,
        push: keyword.notify_push ?? allMessages.channels.push,
      },
      active: keyword.active !== false,
    })),
  ];
};

export const monitoringSounds = (
  preference: MonitoringPreferenceData | null | undefined,
): Record<MonitoringPriority, string> => ({
  alta: preference?.sounds?.alta || DEFAULT_MONITORING_SOUND,
  media: preference?.sounds?.media || DEFAULT_MONITORING_SOUND,
  baixa: preference?.sounds?.baixa || DEFAULT_MONITORING_SOUND,
});

export const desktopNotificationsBlocked = (
  enabled: boolean,
  rules: MonitoringRule[],
  permission: string | undefined,
): boolean =>
  enabled && permission !== 'granted' && rules.some((rule) => rule.active && rule.channels.push);

export const highlightKeywords = (keywords: TradingKeywordTypes[] | undefined): string[] =>
  (keywords || []).filter((keyword) => keyword.active !== false).map((keyword) => keyword.keyword);

export const MAX_KEYWORD_LENGTH = 100;

export const keywordError = (text: string, keywords: TradingKeywordTypes[]): string | null => {
  const keyword = text.trim();
  if (!keyword) return 'Digite a palavra-chave.';
  if (keyword.length > MAX_KEYWORD_LENGTH) {
    return `A palavra-chave pode ter até ${MAX_KEYWORD_LENGTH} caracteres.`;
  }
  if (keywords.some((item) => item.keyword === keyword))
    return 'Esta palavra-chave já está na lista.';

  return null;
};
